<svelte:options accessors={true} />

<script>
    import { onMount } from "svelte";

    import { isMobile, loggedInDoctorDetail, user } from "../../stores";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getBoolean,
        getText,
        goBack,
        replaceURL,
    } from "../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        Button,
        CardLayout,
        Loader,
        NotificationModal,
        PageLoading,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import AccountModifyThirdPartyInsurerModal from "../Account/components/AccountModifyThirdPartyInsurerModal.svelte";

    import {
        addIcon,
        deleteIcon,
        editIcon,
    } from "../../Utils/utilConstants.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    export let params = {};
    export let isCardMode = false;
    export let cardModeConfig = {};
    export let accountThirdPartyInsurers = [];

    let isLoadingThirdPartyInsurers = false;

    let page = 0,
        pageSize = isCardMode ? 999999 : 25,
        count;

    let selectedThirdPartyInsurer,
        modifyThirdPartyInsurerModalOpen = false,
        removeThirdPartyInsurerModalOpen = false;

    let isModifyingThirdPartyInsurer = false;

    const columns = [
        {
            key: "name",
            title: "Name",
            value: (v) => getText(v.name),
            headerClass: "text-left pl-4 text-[14px] w-1/5",
            class: "text-left pl-4 text-[14px]",
        },
        {
            key: "email",
            title: "Email",
            value: (v) => getText(v.email),
            headerClass: "text-left pl-4 text-[14px] w-1/5",
            class: "text-left pl-4 text-[14px]",
        },
        {
            key: "phone",
            title: "Phone",
            value: (v) => getText(v.phone),
            headerClass: "text-left pl-4 text-[14px] w-1/5",
            class: "text-left pl-4 text-[14px]",
        },
        {
            key: "address",
            title: "Address",
            value: (v) => getText(v.address),
            headerClass: "text-left pl-4 text-[14px] w-1/5",
            class: "text-left pl-4 text-[14px]",
        },
        {
            key: "addGst",
            title: "Add GST",
            value: (v) => getBoolean(v.addGST),
            headerClass: "text-left pl-4 text-[14px] w-[10%]",
            class: "text-left pl-4 text-[14px]",
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-4 cursor-default rounded-tr-xl w-[10%]",
            class: "pl-4 cursor-pointer",
            renderValue: () => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    onMount(async () => {
        if (!isUserDoctor && !isCardMode) goBack();

        if (params.query) updateSearchParamsByQuery(params.query);

        await loadThirdPartyInsurers();
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);
            }

            updateURL();
        }
    };

    const loadThirdPartyInsurers = async () => {
        if (!isUserDoctor && !params.doctorId) return;

        isLoadingThirdPartyInsurers = true;

        await reloadThirdPartyInsurers(page, pageSize);

        isLoadingThirdPartyInsurers = false;
    };

    const reloadThirdPartyInsurers = async (
        page = 0,
        pageSize = 25,
        params = _getSearchParams(),
    ) =>
        await apiMethods.getMethod
            .getThirdPartyInsurers(page, pageSize, params)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load account Third Party Insurers, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    accountThirdPartyInsurers = [...res.results];
                    count = res.total;
                }
            });

    const handleRemoveThirdPartyInsurer = async () => {
        if (
            !selectedThirdPartyInsurer ||
            !selectedThirdPartyInsurer.id ||
            isModifyingThirdPartyInsurer
        )
            return;

        isModifyingThirdPartyInsurer = true;

        await apiMethods.deleteMethod
            .deleteAccountThirdPartyInsurer(selectedThirdPartyInsurer)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to remove selected Third Party Insurer, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await reloadThirdPartyInsurers();
            });

        _closeModifyModal();

        isModifyingThirdPartyInsurer = false;
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "buttons") {
            if (className.includes("editIcon")) _openEditModal(rowData);
            else if (className.includes("deleteIcon"))
                _openRemoveModal(rowData);
        }
    };

    const updateURL = () =>
        replaceURL(
            `/ThirdPartyInsurer/page=${page}&size=${pageSize}${_getParamsString()}`,
        );

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        reloadThirdPartyInsurers(0, newPageSize);
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        reloadThirdPartyInsurers(Number(e.detail.firstDataIndex), pageSize);
    };

    // HELPER FUNCTIONS
    const _renderActionIcons =
        () => `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Insurer">
                        <i class="editIcon ${editIcon}"></i>
                    </div>
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Insurer">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;

    const _getSearchParams = () => {
        let para = {
            "filter[doctorId]": isUserDoctor
                ? $loggedInDoctorDetail.id
                : params.doctorId,
        };

        return para;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
        }

        return encodeSearchParams(str);
    };

    export const _openEditModal = (data = {}) => {
        selectedThirdPartyInsurer = { ...data };
        modifyThirdPartyInsurerModalOpen = true;
    };

    const _openRemoveModal = (data) => {
        selectedThirdPartyInsurer = { ...data };
        removeThirdPartyInsurerModalOpen = true;
    };

    const _closeModifyModal = () => {
        selectedThirdPartyInsurer = {};
        modifyThirdPartyInsurerModalOpen = false;
        removeThirdPartyInsurerModalOpen = false;
    };
</script>

{#if isCardMode}
    <CardLayout
        title={cardModeConfig.title ?? "Third Party Insurers"}
        btnOptions={cardModeConfig.btnOptions ?? []}
    >
        {#if isLoadingThirdPartyInsurers}
            <div
                class="flex flex-1 items-center gap-x-4 px-2 py-4 w-full lg:flex-row"
            >
                <Loader />
                <p>Loading Third Party Insurers...</p>
            </div>
        {:else if !!accountThirdPartyInsurers.length}
            <div class="flex flex-1 overflow-auto">
                <SvelteTable
                    {columns}
                    rows={accountThirdPartyInsurers}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                />
            </div>
        {:else}
            <div
                class="flex flex-1 items-center justify-between px-2 py-4 w-full lg:flex-row"
            >
                <p>No Third Party Insurers under account</p>

                <Button clickFunc={() => _openEditModal()}>
                    Add Third Party Insurer
                </Button>
            </div>
        {/if}
    </CardLayout>
{:else}
    <div class="relative flex-1">
        <div class="flex flex-col gap-y-4 absolute inset-0">
            <div class="flex w-full items-center justify-end">
                <button
                    class={`btn btn-sm btn-secondary flex items-center gap-x-2 ${isLoadingThirdPartyInsurers ? "loading" : ""}`}
                    on:click|preventDefault={() => _openEditModal()}
                    disabled={isLoadingThirdPartyInsurers}
                >
                    {#if isLoadingThirdPartyInsurers}
                        <p>Preparing Data</p>
                    {:else}
                        <i class={`${addIcon}`} />
                        <p>Create Third Party Insurer</p>
                    {/if}
                </button>
            </div>

            <div class={$isMobile ? "h-auto w-minus-48" : "relative flex-1"}>
                {#if isLoadingThirdPartyInsurers}
                    <PageLoading />
                {:else}
                    <div
                        class={$isMobile
                            ? "overflow-x-scroll overflow-y-visible"
                            : "overflow-y-auto absolute inset-0"}
                    >
                        <SvelteTable
                            {columns}
                            rows={accountThirdPartyInsurers}
                            clickCellFunc={({
                                detail: { event, row, key },
                            }) => {
                                handleClickCell(event, row, key);
                            }}
                        />
                    </div>
                {/if}
            </div>

            <TableFooter
                {page}
                {pageSize}
                {count}
                tableData={accountThirdPartyInsurers}
                {onPageChange}
                {onPageSizeChange}
                csvConfig={{
                    columns,
                    data: accountThirdPartyInsurers,
                    fileName: "Third Party Insurer",
                    allDataApi: async () =>
                        await apiMethods.getMethod.getThirdPartyInsurers(
                            0,
                            999999,
                            _getSearchParams(),
                        ),
                }}
            />
        </div>
    </div>
{/if}

<AccountModifyThirdPartyInsurerModal
    accountId={isUserDoctor ? $loggedInDoctorDetail.id : params.doctorId}
    insurerDetails={selectedThirdPartyInsurer}
    isModalOpen={modifyThirdPartyInsurerModalOpen}
    onFinishModify={reloadThirdPartyInsurers}
    closeModal={_closeModifyModal}
/>

<NotificationModal
    title="Remove Third Party Insurer"
    isModalOpen={removeThirdPartyInsurerModalOpen}
    cancelText="Delete"
    closeModal={handleRemoveThirdPartyInsurer}
    closeModalFunction={_closeModifyModal}
    submitText="Cancel"
    confirmAction={_closeModifyModal}
    isReverseBtns
    isConfirming={isModifyingThirdPartyInsurer}
>
    Delete selected Third Party Insurer?
</NotificationModal>
